import { useTranslation } from "react-i18next";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import submit from "submit-form";
import SvgIcon from "../../../../../parts/SvgIcon";
import getAvatarUrl from "../../../../../utils/get-avatar-url";
import PhotosCarousel from "./PhotosCarousel";
import TagsDrawer from "./TagsDrawer";

const MealPhotosCard = ({ day, activeUser, athlete, locals, lastItem }) => {
  const { t } = useTranslation("athlete-portal");
  const query = locals?.query ?? {};
  const [imageErrors, setImageErrors] = useState(new Set());
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showTagsDrawer, setShowTagsDrawer] = useState(false);

  // Format date for display with error handling
  const formatDate = (dateNumber) => {
    try {
      const dateStr = dateNumber.toString();
      if (dateStr.length !== 8) {
        throw new Error("Invalid date format");
      }
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const dayNum = dateStr.substring(6, 8);
      const date = moment(`${year}-${month}-${dayNum}`);

      if (!date.isValid()) {
        throw new Error("Invalid date");
      }

      return date.format("MMM D, YYYY");
    } catch (error) {
      console.warn("Error formatting date:", error);
      return t("mealPhotosPage.invalidDate");
    }
  };

  // Get meal time display with error handling
  const getMealTime = (mealPhotos) => {
    if (!mealPhotos?.list || mealPhotos.list.length === 0) return "";

    try {
      const firstPhoto = mealPhotos.list[0];
      if (!firstPhoto?.timestamp) return "";

      const timestamp = moment(firstPhoto.timestamp);
      if (!timestamp.isValid()) return "";

      return timestamp.format("h:mmA");
    } catch (error) {
      console.warn("Error formatting meal time:", error);
      return "";
    }
  };

  // Calculate nutrition totals from day's internalFoodLog

  const getNutritionInfo = () => {
    if (day.nutrients) {
      return {
        protein: Math.round(day.nutrients.proteins || 0),
        carbs: Math.round(day.nutrients.carbs || 0),
        fat: Math.round(day.nutrients.fats || 0),
      };
    }

    return null;
  };
  // Handle image loading errors
  const handleImageError = (photoId) => {
    setImageErrors((prev) => new Set([...prev, photoId]));
  };

  // Collect all photos from all meals with additional metadata
  const getAllPhotos = useCallback(() => {
    const allPhotos = [];
    const mealPhotos = day.mealPhotos || {};

    Object.entries(mealPhotos).forEach(([mealType, mealData]) => {
      if (mealData.list && mealData.list.length > 0) {
        mealData.list.forEach((photo) => {
          allPhotos.push({
            ...photo,
            mealType: mealType,
            mealTags: mealData.tags || [],
          });
        });
      }
    });
    return allPhotos;
  }, [day.mealPhotos]);

  // Open carousel via refresh so server loads mealComments for this day (no re-fetch when switching photos)
  const handlePhotoClick = useCallback(
    (photo) => {
      const allPhotos = getAllPhotos();
      const photoIndex = allPhotos.findIndex((p) => p._id === photo._id);
      setSelectedPhotoIndex(photoIndex >= 0 ? photoIndex : 0);
      setShowCarousel(true);
      submit("/actions/refresh", {
        method: "GET",
        body: {
          dailyStatId: day._id,
          photoIndex: photoIndex >= 0 ? photoIndex : 0,
          photoId: "",
          ...(query?.startDate && { startDate: query.startDate }),
          ...(query?.endDate && { endDate: query.endDate }),
        },
      });
    },
    [day._id, query?.startDate, query?.endDate, getAllPhotos]
  );

  useEffect(() => {
    if (query?.photoId) {
      handlePhotoClick({ _id: query?.photoId });
      delete query?.photoId;
    }

    return () => {};
  }, [handlePhotoClick, query]);

  // Close carousel: refresh without dailyStatId/photoIndex so controller clears mealComments
  const closePhotoCarousel = () => {
    setShowCarousel(false);
    submit("/actions/refresh", {
      method: "GET",
      body: {
        dailyStatId: "",
        photoIndex: "",
        ...(query?.startDate && { startDate: query.startDate }),
        ...(query?.endDate && { endDate: query.endDate }),
      },
    });
  };

  // Handle photo change in carousel
  const handlePhotoChange = (newIndex) => {
    setSelectedPhotoIndex(newIndex);
  };

  // Calculate tag frequencies for the day
  const getTagsData = () => {
    const tagCounts = {};
    let totalTags = 0;
    const mealPhotos = day.mealPhotos || {};

    Object.values(mealPhotos).forEach((mealData) => {
      if (mealData.tags && Array.isArray(mealData.tags)) {
        mealData.tags.forEach((tag) => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          totalTags++;
        });
      }
    });

    // Convert to array and calculate percentages
    const tagsArray = Object.entries(tagCounts).map(([tag, count]) => ({
      tag,
      count,
      percentage: totalTags > 0 ? Math.round((count / totalTags) * 100) : 0,
    }));

    // Sort by count descending
    tagsArray.sort((a, b) => b.count - a.count);

    return { tags: tagsArray, hasData: tagsArray.length > 0 };
  };

  // Toggle tags drawer
  const toggleTagsDrawer = () => {
    setShowTagsDrawer(!showTagsDrawer);
  };

  // Close tags drawer
  const closeTagsDrawer = () => {
    setShowTagsDrawer(false);
  };

  const nutrition = getNutritionInfo();
  const mealPhotos = day.mealPhotos || {};
  const tagsData = getTagsData();

  return (
    <article className={`meal-photos-card ${lastItem ? "last-item" : ""}`}>
      <header className="meal-photos-header">
        <div className="date-section">
          <p>{formatDate(day.utcDate)}</p>
        </div>
        <hgroup className="flex items-center gap-4">
          {nutrition && (
            <div className="meal-photos-nutrition-info">
              <div className="nutrition-item protein">
                <span>{nutrition.protein}</span>
              </div>
              <div className="nutrition-item carbs">
                <span>{nutrition.carbs}</span>
              </div>
              <div className="nutrition-item fat">
                <span>{nutrition.fat}</span>
              </div>
            </div>
          )}
          {tagsData.hasData && (
            <button
              onClick={toggleTagsDrawer}
              disabled={!tagsData.hasData}
              aria-label={t("mealPhotosPage.reasonsForEating")}
              className="why-did-i-eat"
            >
              <SvgIcon
                noFilterOnLight={true}
                src="/img/new-icons/why-eat.svg"
              />
              <span>{t("mealPhotosPage.reasonsForEating")}</span>
            </button>
          )}
        </hgroup>
      </header>

      <div className="meals-container">
        {Object.entries(mealPhotos).map(([mealType, mealData], mealIndex) =>
          mealData.list && mealData.list.length > 0 ? (
            <div
              key={mealType}
              className={`meal-section ${
                mealIndex === Object.entries(mealPhotos).length - 1
                  ? "last-item"
                  : ""
              }`}
            >
              <div className="meal-header">
                <p>{mealType}</p>
                <span className="meal-time">{getMealTime(mealData)}</span>
              </div>
              <div
                className="photos-grid"
                role="grid"
                aria-label={`${mealType} photos`}
              >
                {mealData.list.map((photo, photoIndex) => {
                  const hasError = imageErrors.has(photo._id);

                  const photoThread = photo?.thread;
                  const lastComment = photoThread?.lastComment;
                  const lastCommentUser =
                    lastComment?.sender ??
                    [activeUser, athlete].find(
                      (user) => user?._id === lastComment?.senderId
                    );
                  const avatarUrl = getAvatarUrl(lastCommentUser);

                  return (
                    <div key={photo._id} className="photo-item" role="gridcell">
                      {hasError ? (
                        <div
                          className="photo-error"
                          aria-label={t("mealPhotosPage.imageFailedToLoad")}
                        >
                          <span>📷</span>
                          <span className="sr-only">
                            {t("mealPhotosPage.imageFailedToLoad")}
                          </span>
                        </div>
                      ) : (
                        <div
                          className="photo-item-container"
                          style={{ position: "relative" }}
                        >
                          <img
                            src={photo.image.s3Path}
                            alt={`${mealType} photo taken at ${moment(
                              photo.timestamp
                            ).format("h:mm A")}`}
                            className="meal-photo"
                            loading="lazy"
                            onClick={() => handlePhotoClick(photo)}
                            onError={() => handleImageError(photo._id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handlePhotoClick(photo, mealType);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`View ${mealType} photo in full size`}
                          />
                          {photoThread && (
                            <div className="photo-thread-avatar-wrapper">
                              <img
                                src={avatarUrl}
                                alt="Last comment user avatar"
                                className="photo-thread-avatar"
                                loading="lazy"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null
        )}
      </div>

      {/* Photos Carousel */}
      <PhotosCarousel
        photos={getAllPhotos()}
        selectedPhotoIndex={selectedPhotoIndex}
        isOpen={showCarousel}
        onClose={closePhotoCarousel}
        onPhotoChange={handlePhotoChange}
        date={formatDate(day.utcDate)}
        locals={locals}
        utcDate={day.utcDate}
      />

      {/* Tags Drawer */}

      <TagsDrawer
        isOpen={showTagsDrawer}
        tagsData={tagsData}
        closeTagsDrawer={closeTagsDrawer}
      />
    </article>
  );
};

export default MealPhotosCard;
